import { ErrorHandler, Injectable } from '@angular/core';

const RELOAD_STAMP = 'spectrum.chunkReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError|Loading chunk [^ ]+ failed|Failed to load module script/i;

function isChunkLoadError(error: unknown): boolean {
    const err = error as { message?: string; toString?: () => string } | null;
    const message = err?.message || (err?.toString ? err.toString() : '') || '';
    return CHUNK_ERROR.test(message);
}

/**
 * The timestamp guards against a reload loop: if the chunk is genuinely gone
 * from the server, the second failure inside the cooldown is reported rather
 * than reloaded again.
 */
function canReload(): boolean {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_STAMP) || 0);
        return !last || Date.now() - last > RELOAD_COOLDOWN_MS;
    } catch {
        // Storage disabled (private mode) — reloading blind risks a loop.
        return false;
    }
}

/**
 * Reloads once when a lazy chunk fails to load. Returns true when the reload
 * was triggered, so callers know the error is handled.
 *
 * Screens are lazy chunks with hashed names. After a deploy the old hashes are
 * gone, the SPA fallback answers the chunk request with index.html, and the
 * dynamic import rejects with a MIME-type error — the router cancels the
 * navigation, so the URL never changes and the menu link looks dead. Reloading
 * pulls the new index and bundle, after which the link works.
 */
export function reloadOnChunkError(error: unknown): boolean {
    if (!isChunkLoadError(error) || !canReload()) {
        return false;
    }
    try {
        sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
    } catch {
        return false;
    }
    window.location.reload();
    return true;
}

/**
 * Router hook for `withNavigationErrorHandler` — a failed lazy import surfaces
 * here on every navigation, including the first one after bootstrap.
 */
export function chunkNavigationErrorHandler(error: unknown): void {
    if (!reloadOnChunkError(error)) {
        console.error(error);
    }
}

/** Catches the same failure when it arrives as an uncaught error/rejection. */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {

    handleError(error: unknown): void {
        if (!reloadOnChunkError(error)) {
            console.error(error);
        }
    }
}
